import { useState, useEffect, useMemo } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Clock,
  RefreshCw,
  AlertCircle,
  Bell,
  Shield,
  Settings,
  Megaphone,
  Building2,
  ExternalLink,
  CheckCircle,
  X,
  Loader2
} from 'lucide-react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { collection, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { Vendor, VendorUpdate } from '../../types/vendor'
import { getVendors } from '../../services/vendorService'
import { markEmails } from '../../services/emailService'

interface VendorWithUpdates extends Vendor {
  updates: VendorUpdate[]
  unreadCount: number
}

interface UpdatesTabProps {
  userId: string
  onRefresh?: () => void
}

export default function UpdatesTab({ userId, onRefresh }: UpdatesTabProps) {
  const [vendorsWithUpdates, setVendorsWithUpdates] = useState<VendorWithUpdates[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [scanResult, setScanResult] = useState<string | null>(null)
  const [clearingVendors, setClearingVendors] = useState<Set<string>>(new Set())
  const [exitingVendors, setExitingVendors] = useState<Set<string>>(new Set())
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  useEffect(() => {
    loadVendorsWithUpdates()
  }, [userId])

  const loadVendorsWithUpdates = async () => {
    setLoading(true)
    try {
      // Get all vendors
      const vendors = await getVendors(userId)

      // Get all updates
      const updatesRef = collection(db, 'users', userId, 'updates')
      const updatesQuery = query(
        updatesRef,
        where('isDismissed', '==', false),
        orderBy('receivedAt', 'desc')
      )

      let updates: VendorUpdate[] = []
      try {
        const updatesSnapshot = await getDocs(updatesQuery)
        updates = updatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          receivedAt: doc.data().receivedAt?.toDate?.() || doc.data().receivedAt,
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        })) as VendorUpdate[]
      } catch (indexError) {
        // Fallback if index isn't ready
        console.log('Updates index not ready, fetching all')
        const allUpdatesSnapshot = await getDocs(updatesRef)
        const allUpdates: VendorUpdate[] = allUpdatesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          receivedAt: doc.data().receivedAt?.toDate?.() || doc.data().receivedAt,
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        })) as VendorUpdate[]

        updates = allUpdates
          .filter(u => !u.isDismissed)
          .sort((a, b) => {
            const aDate = a.receivedAt instanceof Date ? a.receivedAt : new Date(0)
            const bDate = b.receivedAt instanceof Date ? b.receivedAt : new Date(0)
            return bDate.getTime() - aDate.getTime()
          })
      }

      // Group updates by vendor
      const vendorUpdateMap = new Map<string, VendorUpdate[]>()
      for (const update of updates) {
        const existing = vendorUpdateMap.get(update.vendorId) || []
        existing.push(update)
        vendorUpdateMap.set(update.vendorId, existing)
      }

      // Create vendor tiles with their updates (exclude vendors marked as human)
      const vendorsWithUpdates: VendorWithUpdates[] = vendors
        .filter(v => v.id && vendorUpdateMap.has(v.id) && !v.isHuman)
        .map(vendor => {
          const vendorUpdates = vendorUpdateMap.get(vendor.id!) || []
          const unreadCount = vendorUpdates.filter(u => !u.isRead).length

          return {
            ...vendor,
            updates: vendorUpdates,
            unreadCount
          }
        })
        .sort((a, b) => {
          // Sort by unread first, then by most recent update
          if (a.unreadCount !== b.unreadCount) {
            return b.unreadCount - a.unreadCount
          }
          const aDate = a.updates[0]?.receivedAt instanceof Date ? a.updates[0].receivedAt : new Date(0)
          const bDate = b.updates[0]?.receivedAt instanceof Date ? b.updates[0].receivedAt : new Date(0)
          return bDate.getTime() - aDate.getTime()
        })

      setVendorsWithUpdates(vendorsWithUpdates)
    } catch (error) {
      console.error('Failed to load vendors with updates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleScanEmails = async () => {
    setIsScanning(true)
    setScanResult(null)
    try {
      const functions = getFunctions()
      const reprocessEmailsForDeals = httpsCallable(functions, 'reprocessEmailsForDeals')
      const result = await reprocessEmailsForDeals({ limit: 100 })
      const data = result.data as { message: string }
      setScanResult(data.message)
      await loadVendorsWithUpdates()
      onRefresh?.()
    } catch (error) {
      console.error('Failed to scan emails:', error)
      setScanResult('Failed to scan emails. Please try again.')
    } finally {
      setIsScanning(false)
    }
  }

  const handleMarkAllCleared = async () => {
    // Collect all email IDs from all updates (remove angle brackets for Firestore doc IDs)
    const allEmailIds: string[] = []
    const vendorIds: string[] = []
    for (const vendor of vendorsWithUpdates) {
      if (vendor.id) vendorIds.push(vendor.id)
      for (const update of vendor.updates) {
        if (update.emailId) {
          allEmailIds.push(update.emailId.replace(/[<>]/g, ''))
        }
      }
    }

    if (allEmailIds.length === 0) return

    if (!confirm(`Clear all ${allEmailIds.length} update emails?`)) return

    // Start clearing animation for all
    setClearingVendors(new Set(vendorIds))

    try {
      const result = await markEmails({
        emailIds: [...new Set(allEmailIds)],
        action: 'cleared',
        deleteOnExpiry: false
      })

      if (result.success) {
        // Start exit animation for all
        setClearingVendors(new Set())
        setExitingVendors(new Set(vendorIds))

        // After exit animation, remove from state
        setTimeout(() => {
          setVendorsWithUpdates([])
          setExitingVendors(new Set())
        }, 300)

        onRefresh?.()
      } else {
        setClearingVendors(new Set())
        setActionResult({ type: 'error', message: 'Failed to clear emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to clear emails:', error)
      setClearingVendors(new Set())
      setActionResult({ type: 'error', message: 'Failed to clear emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleMarkVendorCleared = async (vendorId: string) => {
    // Get all email IDs for this vendor from the updates we have loaded
    const vendor = vendorsWithUpdates.find(v => v.id === vendorId)
    if (!vendor) {
      setActionResult({ type: 'error', message: 'Vendor not found' })
      setTimeout(() => setActionResult(null), 3000)
      return
    }

    // Remove angle brackets from emailIds to match Firestore doc IDs
    const emailIds = [...new Set(vendor.updates.filter(u => u.emailId).map(u => u.emailId!.replace(/[<>]/g, '')))]

    if (emailIds.length === 0) {
      setActionResult({ type: 'error', message: 'No emails found for this vendor' })
      setTimeout(() => setActionResult(null), 3000)
      return
    }

    // Start clearing animation
    setClearingVendors(prev => new Set(prev).add(vendorId))

    try {
      const result = await markEmails({
        emailIds,
        action: 'cleared',
        deleteOnExpiry: false
      })

      if (result.success) {
        // Start exit animation
        setClearingVendors(prev => {
          const next = new Set(prev)
          next.delete(vendorId)
          return next
        })
        setExitingVendors(prev => new Set(prev).add(vendorId))

        // After exit animation, remove from state
        setTimeout(() => {
          setVendorsWithUpdates(prev => prev.filter(v => v.id !== vendorId))
          setExitingVendors(prev => {
            const next = new Set(prev)
            next.delete(vendorId)
            return next
          })
        }, 300)

        onRefresh?.()
      } else {
        setClearingVendors(prev => {
          const next = new Set(prev)
          next.delete(vendorId)
          return next
        })
        setActionResult({ type: 'error', message: 'Failed to clear emails' })
        setTimeout(() => setActionResult(null), 3000)
      }
    } catch (error) {
      console.error('Failed to clear vendor emails:', error)
      setClearingVendors(prev => {
        const next = new Set(prev)
        next.delete(vendorId)
        return next
      })
      setActionResult({ type: 'error', message: 'Failed to clear emails' })
      setTimeout(() => setActionResult(null), 3000)
    }
  }

  const handleMarkAsRead = async (updateId: string) => {
    try {
      const updateRef = doc(db, 'users', userId, 'updates', updateId)
      await updateDoc(updateRef, { isRead: true })
      await loadVendorsWithUpdates()
    } catch (error) {
      console.error('Failed to mark update as read:', error)
    }
  }

  const handleDismiss = async (updateId: string) => {
    try {
      const updateRef = doc(db, 'users', userId, 'updates', updateId)
      await updateDoc(updateRef, { isDismissed: true })
      await loadVendorsWithUpdates()
    } catch (error) {
      console.error('Failed to dismiss update:', error)
    }
  }

  const stats = useMemo(() => ({
    totalVendors: vendorsWithUpdates.length,
    totalUpdates: vendorsWithUpdates.reduce((sum, v) => sum + v.updates.length, 0),
    unreadUpdates: vendorsWithUpdates.reduce((sum, v) => sum + v.unreadCount, 0)
  }), [vendorsWithUpdates])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              Service Updates
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {stats.totalUpdates} updates from {stats.totalVendors} companies
              {stats.unreadUpdates > 0 && (
                <span className="ml-2 text-amber-600 font-medium">
                  ({stats.unreadUpdates} unread)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {vendorsWithUpdates.length > 0 && (
              <button
                onClick={handleMarkAllCleared}
                disabled={clearingVendors.size > 0 || isScanning}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 min-h-[44px] text-sm font-medium rounded-lg border border-green-300 text-green-700 hover:bg-green-50 active:bg-green-100 disabled:opacity-50 transition-colors"
              >
                {clearingVendors.size > 0 ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                <span className="hidden xs:inline sm:inline">Clear All</span>
                <span className="xs:hidden sm:hidden">Clear</span>
              </button>
            )}
            <button
              onClick={handleScanEmails}
              disabled={isScanning || clearingVendors.size > 0}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 min-h-[44px] text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{isScanning ? 'Scanning...' : 'Scan for Updates'}</span>
              <span className="sm:hidden">{isScanning ? 'Scanning...' : 'Scan'}</span>
            </button>
          </div>
        </div>
        {(scanResult || actionResult) && (
          <div className={`mt-3 p-3 rounded-lg text-xs sm:text-sm ${
            (scanResult?.includes('Failed') || actionResult?.type === 'error')
              ? 'bg-red-50 text-red-700'
              : 'bg-green-50 text-green-700'
          }`}>
            {actionResult?.message || scanResult}
          </div>
        )}
      </div>

      {/* Updates List */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {vendorsWithUpdates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 px-4">
            <Bell className="w-10 h-10 sm:w-12 sm:h-12 mb-4 opacity-50" />
            <p className="text-base sm:text-lg font-medium text-center">No service updates</p>
            <p className="text-xs sm:text-sm mt-1 text-center">Policy changes and announcements will appear here</p>
          </div>
        ) : (
          <div className="space-y-3 transition-all duration-300">
            {vendorsWithUpdates
              .filter(vendor => !exitingVendors.has(vendor.id!) || clearingVendors.has(vendor.id!))
              .map(vendor => (
              <VendorUpdateTile
                key={vendor.id}
                vendor={vendor}
                isExpanded={expandedVendor === vendor.id}
                isClearing={clearingVendors.has(vendor.id!)}
                isExiting={exitingVendors.has(vendor.id!)}
                onToggle={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id!)}
                onMarkAsRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                onClearVendor={(vendorId) => handleMarkVendorCleared(vendorId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface VendorUpdateTileProps {
  vendor: VendorWithUpdates
  isExpanded: boolean
  isClearing: boolean
  isExiting: boolean
  onToggle: () => void
  onMarkAsRead: (updateId: string) => void
  onDismiss: (updateId: string) => void
  onClearVendor: (vendorId: string) => void
}

/**
 * Vendor logo component with graceful fallback
 */
function VendorLogo({ vendor }: { vendor: VendorWithUpdates }) {
  const [imgError, setImgError] = useState(false)

  if (vendor.logoUrl && !imgError) {
    return (
      <img
        src={vendor.logoUrl}
        alt={vendor.name}
        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-contain bg-white p-1"
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-lg ${
      vendor.unreadCount > 0 ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gray-400'
    }`}>
      {vendor.name.charAt(0).toUpperCase()}
    </div>
  )
}

function VendorUpdateTile({
  vendor,
  isExpanded,
  isClearing,
  isExiting,
  onToggle,
  onMarkAsRead,
  onDismiss,
  onClearVendor
}: VendorUpdateTileProps) {
  const latestUpdate = vendor.updates[0]
  const latestDate = latestUpdate?.receivedAt instanceof Date ? latestUpdate.receivedAt : new Date(0)

  const formatDate = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const getUpdateTypeIcon = (type: VendorUpdate['updateType']) => {
    switch (type) {
      case 'policy':
        return <Shield size={14} className="text-blue-500 sm:w-4 sm:h-4" />
      case 'security':
        return <AlertCircle size={14} className="text-red-500 sm:w-4 sm:h-4" />
      case 'feature':
        return <Settings size={14} className="text-green-500 sm:w-4 sm:h-4" />
      case 'maintenance':
        return <Settings size={14} className="text-amber-500 sm:w-4 sm:h-4" />
      case 'announcement':
        return <Megaphone size={14} className="text-purple-500 sm:w-4 sm:h-4" />
      default:
        return <Bell size={14} className="text-gray-500 sm:w-4 sm:h-4" />
    }
  }

  const getImportanceBadge = (importance: VendorUpdate['importance']) => {
    switch (importance) {
      case 'high':
        return <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-red-100 text-red-700 rounded-full">Important</span>
      case 'medium':
        return <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Notable</span>
      default:
        return null
    }
  }

  return (
    <div className={`bg-white rounded-xl border-2 transition-all duration-300 ${
      isExiting ? 'opacity-0 scale-95 pointer-events-none' :
      isClearing ? 'animate-pulse ring-2 ring-green-400' :
      isExpanded ? 'border-amber-300 shadow-lg' :
      vendor.unreadCount > 0 ? 'border-amber-200' :
      'border-gray-200 hover:border-gray-300'
    }`}>
      {/* Tile Header */}
      <div
        className="p-3 sm:p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          {/* Vendor Logo */}
          <div className="flex-shrink-0">
            <VendorLogo vendor={vendor} />
          </div>

          {/* Vendor Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate max-w-[150px] sm:max-w-none">{vendor.name}</h3>
              {vendor.unreadCount > 0 && (
                <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-amber-100 text-amber-700 rounded-full whitespace-nowrap">
                  {vendor.unreadCount} new
                </span>
              )}
            </div>

            {/* Latest update preview */}
            {latestUpdate && (
              <div className="flex items-center gap-2 mt-1">
                {getUpdateTypeIcon(latestUpdate.updateType)}
                <p className="text-xs sm:text-sm text-gray-600 truncate">{latestUpdate.title}</p>
              </div>
            )}

            {/* Meta info */}
            <div className="flex items-center gap-2 sm:gap-3 mt-2 text-[10px] sm:text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Bell size={10} className="sm:w-3 sm:h-3" />
                {vendor.updates.length} update{vendor.updates.length !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Clock size={10} className="sm:w-3 sm:h-3" />
                {formatDate(latestDate)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 flex items-center gap-1 sm:gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                vendor.id && onClearVendor(vendor.id)
              }}
              disabled={isClearing}
              className={`p-2 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center rounded-lg transition-colors ${
                isClearing
                  ? 'text-green-600 bg-green-50'
                  : 'text-gray-400 hover:text-green-600 hover:bg-green-50 active:bg-green-100'
              }`}
              title="Clear"
            >
              {isClearing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <CheckCircle size={18} />
              )}
            </button>
            <div className="text-gray-400 p-1">
              {isExpanded ? <ChevronDown size={20} className="sm:w-6 sm:h-6" /> : <ChevronRight size={20} className="sm:w-6 sm:h-6" />}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Company Info */}
          {vendor.companyInfo && (
            <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Building2 size={12} className="sm:w-3.5 sm:h-3.5" />
                  <span className="font-medium">{vendor.companyInfo.industry}</span>
                </div>
                {vendor.domain && (
                  <>
                    <span className="hidden sm:inline text-gray-300">|</span>
                    <a
                      href={`https://${vendor.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 active:text-indigo-900 flex items-center gap-1 ml-5 sm:ml-0"
                      onClick={e => e.stopPropagation()}
                    >
                      {vendor.domain}
                      <ExternalLink size={10} className="sm:w-3 sm:h-3" />
                    </a>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Updates List */}
          <div className="max-h-80 sm:max-h-96 overflow-y-auto divide-y divide-gray-100">
            {vendor.updates.map(update => (
              <UpdateCard
                key={update.id}
                update={update}
                formatDate={formatDate}
                getUpdateTypeIcon={getUpdateTypeIcon}
                getImportanceBadge={getImportanceBadge}
                onMarkAsRead={() => update.id && onMarkAsRead(update.id)}
                onDismiss={() => update.id && onDismiss(update.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface UpdateCardProps {
  update: VendorUpdate
  formatDate: (date: Date) => string
  getUpdateTypeIcon: (type: VendorUpdate['updateType']) => JSX.Element
  getImportanceBadge: (importance: VendorUpdate['importance']) => JSX.Element | null
  onMarkAsRead: () => void
  onDismiss: () => void
}

function UpdateCard({
  update,
  formatDate,
  getUpdateTypeIcon,
  getImportanceBadge,
  onMarkAsRead,
  onDismiss
}: UpdateCardProps) {
  const receivedDate = update.receivedAt instanceof Date ? update.receivedAt : new Date(0)

  return (
    <div className={`p-3 sm:p-4 ${update.isRead ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex-1 min-w-0">
          {/* Header with type and importance */}
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
            {getUpdateTypeIcon(update.updateType)}
            <span className="text-[10px] sm:text-xs text-gray-500 capitalize">{update.updateType}</span>
            {getImportanceBadge(update.importance)}
          </div>

          {/* Title */}
          <h4 className={`font-medium text-sm sm:text-base ${update.isRead ? 'text-gray-700' : 'text-gray-900'}`}>
            {update.title}
          </h4>

          {/* Summary */}
          <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2 sm:line-clamp-none">{update.summary}</p>

          {/* Date */}
          <div className="flex items-center gap-1 mt-2 text-[10px] sm:text-xs text-gray-500">
            <Clock size={10} className="sm:w-3 sm:h-3" />
            {formatDate(receivedDate)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1">
          {!update.isRead && (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkAsRead(); }}
              className="p-2 sm:p-1.5 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-green-50 active:bg-green-100 rounded-lg transition-colors"
              title="Mark as read"
            >
              <CheckCircle size={18} />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="p-2 sm:p-1.5 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors"
            title="Dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}
